import { INDENT_EM_PER_LEVEL } from '@root/helpers/editor';
import type JSZip from 'jszip';

// mammoth resolves Word numbering to plain <ol>/<ul> and discards the list format
// (lowerRoman, lowerLetter, decimal). Recover it straight from the OOXML parts so the
// editor can render the source marker glyph.
const OOXML_FMT_TO_EDITOR_TYPE: Record<string, string> = {
  decimal: '1',
  lowerRoman: 'i',
  lowerLetter: 'a',
  upperRoman: 'I',
  upperLetter: 'A',
};

// w:lvlText carries the marker punctuation around the number placeholder — "%1)" or "(%1)"
type OrderedListMarker = 'paren' | 'parens';

const MARKER_BY_LVL_TEXT_SHAPE: [RegExp, OrderedListMarker][] = [
  [/^\(%\d+\)$/, 'parens'],
  [/^%\d+\)$/, 'paren'],
];

const markerOf = (lvlText: string | undefined): OrderedListMarker | undefined =>
  lvlText ? MARKER_BY_LVL_TEXT_SHAPE.find(([shape]) => shape.test(lvlText))?.[1] : undefined;

export interface OrderedListFormat {
  type: string;
  marker?: OrderedListMarker;
  // the item's visible ordinal within its numbering instance — continuation lists start past 1
  start: number;
  ilvl: number;
}

interface NumberingInstance {
  abstractId: string;
  startOverrides: Record<string, number>;
}

const numberingInstances = (numberingXml: string): Record<string, NumberingInstance> => {
  const map: Record<string, NumberingInstance> = {};
  for (const num of numberingXml.matchAll(/<w:num\s+w:numId="(\d+)"[^>]*>([\s\S]*?)<\/w:num>/g)) {
    const abstract = /<w:abstractNumId w:val="(\d+)"/.exec(num[2]);
    if (!abstract) continue;

    const startOverrides: Record<string, number> = {};
    for (const override of num[2].matchAll(/<w:lvlOverride\s+w:ilvl="(\d+)"[^>]*>([\s\S]*?)<\/w:lvlOverride>/g)) {
      const start = /<w:startOverride w:val="(\d+)"/.exec(override[2]);
      if (start) startOverrides[override[1]] = Number(start[1]);
    }

    map[num[1]] = { abstractId: abstract[1], startOverrides };
  }
  return map;
};

interface LevelFormat {
  fmt: string;
  lvlText?: string;
  start: number;
}

const abstractLevelFormats = (numberingXml: string): Record<string, Record<string, LevelFormat>> => {
  const map: Record<string, Record<string, LevelFormat>> = {};
  for (const abstract of numberingXml.matchAll(
    /<w:abstractNum\s+w:abstractNumId="(\d+)"[^>]*>([\s\S]*?)<\/w:abstractNum>/g,
  )) {
    const levels: Record<string, LevelFormat> = {};
    for (const lvl of abstract[2].matchAll(/<w:lvl\s+w:ilvl="(\d+)"[^>]*>([\s\S]*?)<\/w:lvl>/g)) {
      const fmt = /<w:numFmt w:val="([^"]+)"/.exec(lvl[2]);
      const lvlText = /<w:lvlText w:val="([^"]+)"/.exec(lvl[2]);
      const start = /<w:start w:val="(\d+)"/.exec(lvl[2]);
      if (fmt) levels[lvl[1]] = { fmt: fmt[1], lvlText: lvlText?.[1], start: start ? Number(start[1]) : 1 };
    }
    map[abstract[1]] = levels;
  }
  return map;
};

// mammoth renders a numbered paragraph with a heading style as <h1>-<h6>, not <li>,
// so it must not claim a slot. Built-in ids match Heading[1-6] directly; localised
// templates are caught via the style's canonical "heading N" name in styles.xml.
const headingStyleIds = (stylesXml: string): Set<string> => {
  const ids = new Set<string>();
  for (const style of stylesXml.matchAll(/<w:style\s[^>]*w:styleId="([^"]+)"[^>]*>([\s\S]*?)<\/w:style>/g)) {
    if (/<w:name w:val="heading [1-6]"/i.test(style[2])) ids.add(style[1]);
  }
  return ids;
};

// One entry per ordered (non-bullet) numbered paragraph, in document order.
// null marks a numbered paragraph whose format the editor can't render — the slot is
// kept so the result still aligns one-to-one with the converted <ol> items.
const resolveOrderedFormats = (
  numberingXml: string,
  documentXml: string,
  stylesXml = '',
): (OrderedListFormat | null)[] => {
  const instances = numberingInstances(numberingXml);
  const abstractFormats = abstractLevelFormats(numberingXml);
  const headingIds = headingStyleIds(stylesXml);

  // Word restarts each deeper level when a shallower item of the same instance arrives,
  // so consuming an ordinal resets every deeper counter of that instance.
  const counters = new Map<string, number>();
  const consumeOrdinal = (numId: string, ilvl: number, startBase: number): number => {
    for (const key of counters.keys()) {
      const [id, level] = key.split(':');
      if (id === numId && Number(level) > ilvl) counters.delete(key);
    }

    const key = `${numId}:${ilvl}`;
    const consumed = counters.get(key) ?? 0;
    counters.set(key, consumed + 1);
    return startBase + consumed;
  };

  const result: (OrderedListFormat | null)[] = [];
  for (const paragraph of documentXml.matchAll(/<w:p\b[\s\S]*?<\/w:p>/g)) {
    const numId = /<w:numPr>[\s\S]*?<w:numId w:val="(\d+)"/.exec(paragraph[0])?.[1];
    if (!numId) continue;

    const ilvl = Number(/<w:ilvl w:val="(\d+)"/.exec(paragraph[0])?.[1] ?? '0');
    const instance = instances[numId];
    const level = instance && abstractFormats[instance.abstractId]?.[ilvl];

    if (!level || level.fmt === 'bullet') continue;

    const start = consumeOrdinal(numId, ilvl, instance.startOverrides[ilvl] ?? level.start);

    const styleId = /<w:pStyle w:val="([^"]+)"/.exec(paragraph[0])?.[1];
    if (styleId && (headingIds.has(styleId) || /^Heading[1-6]$/.test(styleId))) continue;

    const type = OOXML_FMT_TO_EDITOR_TYPE[level.fmt];
    result.push(type ? { type, marker: markerOf(level.lvlText), start, ilvl } : null);
  }
  return result;
};

export const extractOrderedListFormats = async (zipArchive: Promise<JSZip>): Promise<(OrderedListFormat | null)[]> => {
  const zip = await zipArchive;
  const numberingXml = await zip.file('word/numbering.xml')?.async('string');
  const documentXml = await zip.file('word/document.xml')?.async('string');
  const stylesXml = await zip.file('word/styles.xml')?.async('string');

  if (!numberingXml || !documentXml) return [];

  return resolveOrderedFormats(numberingXml, documentXml, stylesXml);
};

// Blank lines between numbered items interrupt mammoth's consecutive-list grouping, splitting one
// list into single-item <ol>s that each restart at 1 — re-join same-kind lists across them.
const isEmptyParagraph = (node: Element): boolean =>
  node.tagName === 'P' && (node.textContent ?? '').trim() === '' && node.querySelector('img, br') === null;

export const mergeSplitLists = (doc: Document): void => {
  doc.querySelectorAll('ol, ul').forEach((list) => {
    // querySelectorAll is live-safe here: already-absorbed lists are removed before we reach them
    if (!list.isConnected) return;

    const pending: Element[] = [];
    let sibling = list.nextElementSibling;

    while (sibling) {
      if (isEmptyParagraph(sibling)) {
        pending.push(sibling);
        sibling = sibling.nextElementSibling;
        continue;
      }
      if (sibling.tagName === list.tagName) {
        pending.forEach((p) => p.remove());
        pending.length = 0;
        while (sibling.firstChild) {
          list.appendChild(sibling.firstChild);
        }
        const absorbed = sibling;
        sibling = sibling.nextElementSibling;
        absorbed.remove();
        continue;
      }
      break;
    }
  });
};

// mammoth emits two list shapes the editor schema rejects — a list as a direct child of a list,
// and an <li> whose only content is a sublist. ProseMirror silently "repairs" both by flattening,
// merging or re-wrapping as bullets; fix the shapes up front so nesting and type survive the parse.
export const normaliseHtmlLists = (doc: Document): void => {
  // mammoth wraps interrupted sub-lists in a bullet list; unwrap it or it renders a stray empty bullet
  doc.body.querySelectorAll('ul').forEach((wrapper) => {
    const items = [...wrapper.children];
    const holdsOnlyLists = (item: Element) =>
      item.tagName === 'LI' &&
      item.children.length > 0 &&
      [...item.children].every((child) => child.tagName === 'OL' || child.tagName === 'UL') &&
      (item.textContent ?? '').trim() ===
        [...item.children]
          .map((child) => child.textContent ?? '')
          .join('')
          .trim();

    if (items.length > 0 && items.every(holdsOnlyLists)) {
      wrapper.replaceWith(...items.flatMap((item) => [...item.children]));
    }
  });

  doc.body.querySelectorAll('ol > ol, ol > ul, ul > ol, ul > ul').forEach((inner) => {
    const prev = inner.previousElementSibling;
    if (prev?.tagName === 'LI') {
      prev.appendChild(inner);
    } else {
      const li = doc.createElement('li');
      inner.parentNode?.insertBefore(li, inner);
      li.appendChild(doc.createElement('p'));
      li.appendChild(inner);
    }
  });

  let changed = true;
  while (changed) {
    changed = false;
    doc.body.querySelectorAll('li').forEach((li) => {
      const onlyChild = li.children.length === 1 ? li.firstElementChild : null;
      const isBareSublist =
        onlyChild &&
        (onlyChild.tagName === 'OL' || onlyChild.tagName === 'UL') &&
        (li.textContent ?? '').trim() === (onlyChild.textContent ?? '').trim();
      if (!isBareSublist) return;

      const prevLi = li.previousElementSibling;
      if (prevLi?.tagName === 'LI') {
        prevLi.appendChild(onlyChild);
        li.remove();
        changed = true;
      } else if (!li.querySelector(':scope > p')) {
        li.insertBefore(doc.createElement('p'), onlyChild);
      }
    });
  }
};

// If `formats` does not align one-to-one with the converted <ol> items the correlation is unsafe —
// leave the HTML untouched; a wrong marker is worse than the decimal default.
const isFirstItem = (li: Element): boolean => !li.previousElementSibling || li.previousElementSibling.tagName !== 'LI';

export const applyListTypes = (doc: Document, formats: (OrderedListFormat | null)[]): void => {
  const orderedItems = [...doc.querySelectorAll('li')].filter((li) => li.parentElement?.tagName === 'OL');

  if (orderedItems.length !== formats.length) return;

  orderedItems.forEach((li, index) => {
    const format = formats[index];
    if (!format) return;

    let list = li.parentElement!;
    const marker = `${format.marker ?? 'dot'}-${format.type}`;

    // mammoth concatenates adjacent lists of different numbering instances into one <ol>;
    // a mid-list format change or a fresh instance (ordinal back at 1) marks the seam
    if (!isFirstItem(li) && (format.start === 1 || marker !== list.getAttribute('data-marker'))) {
      const split = doc.createElement('ol');
      list.after(split);
      let node: Element | null = li;
      while (node) {
        const next: Element | null = node.nextElementSibling;
        split.appendChild(node);
        node = next;
      }
      list = split;
    }

    if (isFirstItem(li)) {
      list.setAttribute('type', format.type);

      // the editor draws markers itself (li::before + an `item` counter in contract.css), so the
      // marker rides in data-marker with its counter style — ol[type] matches case-insensitively
      // in CSS and native start is never consulted; offsets need the inline counter-reset
      list.setAttribute('data-marker', marker);

      if (format.start > 1) {
        list.setAttribute('start', String(format.start));
        list.style.counterReset = `item ${format.start - 1}`;
      }

      if (format.ilvl > 0 && !list.closest('li')) {
        list.style.paddingLeft = `${format.ilvl * INDENT_EM_PER_LEVEL}em`;
      }
    }
  });
};
